import type { MatrixOfDestiny } from '@/api/services/numer'

export type NumColor =
  | 'default'
  | 'yellow'
  | 'violet'
  | 'red'
  | 'green'
  | 'blue'
  | 'cian'
  | 'orange'

export type MatrixPoint = keyof MatrixOfDestiny

export type MatrixGroup = {
  id: string
  title: string
  sections: MatrixSection[]
}

export type MatrixSection = {
  id: string
  title: string
  description: string
  question: string
  points: MatrixPoint[]
}

export const MATRIX_POINT_COLORS: Partial<Record<MatrixPoint, NumColor>> = {
  dayArcana: 'violet',
  monthArcana: 'violet',
  yearArcana: 'red',
  karmaArcana: 'red',
  center: 'yellow',

  personalityOuter: 'blue',
  personality: 'cian',
  personalityInner: 'green',
  skyTopOuter: 'blue',
  skyTop: 'cian',
  skyTopInner: 'green',
  forWorld: 'orange',
  skyBottom: 'orange',

  fatherLine: 'default',
  f1: 'default',
  f2: 'default',
  motherLine: 'default',
  g1: 'default',
  g2: 'default',
  future: 'default',
  h1: 'default',
  h2: 'default',
  past: 'default',
  i1: 'default',
  i2: 'default',

  forWorldOuter: 'default',
  skyBottomOuter: 'default',
  socialPurpose: 'default',
  spiritualPurpose: 'default',
  balanceCenter: 'default',
  moneyCenter: 'default',
  relationshipCenter: 'default',
}

export const MATRIX_MONEY_SECTION: MatrixSection = {
  id: 'moneyCode',
  title: 'Мой денежный код',
  description: 'Что помогает тебе притягивать деньги, а что мешает.',
  question:
    'Расскажи про мой денежный код: вход — {points[0]}, центр — {points[1]}, баланс — {points[2]}. Какая главная энергия движет моими финансами и как мне с ней работать?',
  points: ['forWorld', 'moneyCenter', 'balanceCenter'],
}

export const MATRIX_MONEY_GROUP: MatrixGroup = {
  id: 'money',
  title: 'Реализация',
  sections: [
    MATRIX_MONEY_SECTION,
    {
      id: 'earthChannel',
      title: 'Как я зарабатываю',
      description: 'Как тебе проще зарабатывать и через что приходят деньги.',
      question:
        'Расскажи про мой земной канал: основа — {points[0]}, проявление — {points[1]}. Как я зарабатываю и как мне заземляться в материальном?',
      points: ['skyBottom', 'skyBottomOuter'],
    },
    {
      id: 'workTalents',
      title: 'Мои таланты в работе',
      description: 'В чём ты особенно сильна и где это можно применить.',
      question:
        'Расскажи про мои таланты в работе: личность — {points[0]}, внешнее проявление — {points[1]}. Какие у меня сильные стороны для дела и как их использовать?',
      points: ['personality', 'personalityOuter'],
    },
    {
      id: 'socialRealization',
      title: 'Призвание в социуме',
      description: 'В какой роли ты можешь быть полезна людям и востребована.',
      question:
        'Расскажи про моё призвание в социуме: предназначение — {points[0]}, проявление — {points[1]}. Где я могу раскрыться материально?',
      points: ['socialPurpose', 'forWorld'],
    },
    {
      id: 'successEnergy',
      title: 'Энергия успеха',
      description: 'Что помогает тебе расти и добиваться большего.',
      question:
        'Расскажи про мою энергию успеха: вектор будущего — {points[0]}, точка опоры — {points[1]}. Куда мне двигаться и что даст мне развитие?',
      points: ['future', 'balanceCenter'],
    },
    {
      id: 'visibilityChannel',
      title: 'Канал известности',
      description: 'За что тебя замечают и что помогает получить признание.',
      question:
        'Расскажи про мой канал известности: внешнее проявление в социуме — {points[0]}, моё призвание для мира — {points[1]}. Как меня видит мир и через что ко мне приходит признание?',
      points: ['forWorldOuter', 'forWorld'],
    },
    {
      id: 'moneyAndMission',
      title: 'Деньги и миссия',
      description: 'Как связать то, что тебе нравится, с хорошим заработком.',
      question:
        'Расскажи как соединить деньги и миссию: денежный код — {points[0]}, духовная миссия — {points[1]}. Как мне зарабатывать через своё истинное предназначение, а не вопреки ему?',
      points: ['moneyCenter', 'spiritualPurpose'],
    },
  ],
}

export const MATRIX_LOVE_SECTION: MatrixSection = {
  id: 'loveHeart',
  title: 'Сердце моих отношений',
  description: 'Что для тебя главное в любви и без чего отношения не работают.',
  question:
    'Расскажи про сердце моих отношений: вход — {points[0]}, центр — {points[1]}, баланс — {points[2]}. Какая главная энергия движет моей любовью?',
  points: ['skyBottom', 'relationshipCenter', 'balanceCenter'],
}

export const MATRIX_LOVE_GROUP: MatrixGroup = {
  id: 'love',
  title: 'Любовь',
  sections: [
    MATRIX_LOVE_SECTION,
    {
      id: 'idealPartner',
      title: 'Какой партнёр мне нужен',
      description: 'С каким человеком тебе будет хорошо и чего ты ждёшь от отношений.',
      question:
        'Расскажи про энергии моего партнёрства — {points[0]} и {points[1]}. Какой партнёр мне нужен и с кем мне по-настоящему хорошо?',
      points: ['h1', 'h2'],
    },
    {
      id: 'unionFuture',
      title: 'Союз и совместное будущее',
      description: 'Какими могут быть твои отношения и куда они способны привести.',
      question:
        'Расскажи про энергии нашего союза — {points[0]} и {points[1]}. Куда движутся мои отношения и какое у нас совместное будущее?',
      points: ['i1', 'i2'],
    },
    {
      id: 'loveKarma',
      title: 'Любовь и карма',
      description: 'Почему тебе встречаются определённые партнёры и какие уроки они приносят.',
      question:
        'Расскажи про карму моей любви: центр отношений — {points[0]}, кармический урок — {points[1]}. Почему в мою жизнь приходят именно такие партнёры?',
      points: ['relationshipCenter', 'karmaArcana'],
    },
    {
      id: 'femMascBalance',
      title: 'Женское и мужское во мне',
      description: 'Что тебя притягивает в партнёрах и кого ты притягиваешь сама.',
      question:
        'Расскажи про баланс женского и мужского во мне: мама — {points[0]}, папа — {points[1]}. Какая моя внутренняя женско-мужская энергия и каких партнёров она притягивает?',
      points: ['motherLine', 'fatherLine'],
    },
    {
      id: 'partnerVsUnion',
      title: 'Партнёр и союз',
      description: 'Каких отношений ты хочешь и что получается в реальности.',
      question:
        'Расскажи про разницу между моим партнёром и нашим союзом: качества партнёра — {points[0]} и {points[1]}, энергия союза — {points[2]} и {points[3]}. Почему то что я хочу в партнёре не всегда совпадает с тем что складывается в союзе?',
      points: ['h1', 'h2', 'i1', 'i2'],
    },
    {
      id: 'loveAndSelf',
      title: 'Любовь к себе',
      description: 'Как отношение к себе влияет на твой выбор партнёров и отношения с ними.',
      question:
        'Расскажи как моя любовь к себе влияет на отношения: главный аркан — {points[0]}, центр отношений — {points[1]}. Как моя самооценка и принятие себя формируют отношения с партнёрами?',
      points: ['center', 'relationshipCenter'],
    },
  ],
}

export const MATRIX_KARMA_SECTION: MatrixSection = {
  id: 'karmaLesson',
  title: 'Кармический хвост',
  description: 'Какой урок тебе важно пройти, чтобы двигаться дальше.',
  question:
    'Расскажи про мой кармический хвост: корень — {points[0]}, урок — {points[1]}, проявление — {points[2]}. Что мне важно проработать в этой жизни и почему?',
  points: ['karmaArcana', 'skyBottomOuter', 'skyBottom'],
}

export const MATRIX_MISSION_GROUP: MatrixGroup = {
  id: 'purpose',
  title: 'Миссия',
  sections: [
    {
      id: 'heavenChannel',
      title: 'Небесный канал',
      description: 'Твои внутренние ориентиры, интуиция и духовная сторона.',
      question:
        'Расскажи про мой небесный канал: основа — {points[0]}, внутри — {points[1]}, проявление — {points[2]}. Какие у меня духовные дары и связь с высшим?',
      points: ['skyTop', 'skyTopInner', 'skyTopOuter'],
    },
    MATRIX_KARMA_SECTION,
    {
      id: 'mission',
      title: 'Моя миссия в этом мире',
      description: 'В чём твоя главная задача и куда тебе важно двигаться.',
      question: 'Расскажи про мою духовную миссию — {points[0]}. В чём моя главная задача в этой жизни?',
      points: ['spiritualPurpose'],
    },
    {
      id: 'giveToWorld',
      title: 'Что я могу дать людям',
      description: 'Чем ты можешь быть полезна другим и что у тебя получается особенно хорошо.',
      question:
        'Расскажи про моё социальное предназначение: внутренне — {points[0]}, как я проявляюсь — {points[1]}, что вижу снаружи — {points[2]}. Что я могу дать людям?',
      points: ['socialPurpose', 'forWorld', 'forWorldOuter'],
    },
    {
      id: 'lifeVector',
      title: 'Откуда я пришла, куда иду',
      description: 'Что ты несёшь из прошлого и к чему тебе важно прийти.',
      question:
        'Расскажи про мой жизненный вектор: прошлое — {points[0]}, будущее — {points[1]}. Какой опыт я несу из прошлого и куда мне двигаться?',
      points: ['past', 'future'],
    },
    {
      id: 'twoMissions',
      title: 'Две мои миссии',
      description: 'Как сочетаются то, что важно лично тебе, и то, что ты можешь дать другим.',
      question: 'Расскажи про мои две миссии: духовная — {points[0]}, социальная — {points[1]}. Как они связаны и почему важны вместе?',
      points: ['spiritualPurpose', 'socialPurpose'],
    },
    {
      id: 'karmaThroughTime',
      title: 'Карма через время',
      description: 'Какие прошлые сценарии могут повторяться и что важно изменить.',
      question:
        'Расскажи как моя карма проявляется во времени: кармический урок — {points[0]}, прошлое — {points[1]}, будущее — {points[2]}. Как этот урок влиял на мой прошлый опыт и что важно изменить в будущем?',
      points: ['karmaArcana', 'past', 'future'],
    },
  ],
}

export const MATRIX_GROUPS: MatrixGroup[] = [
  {
    id: 'self',
    title: 'Кто я',
    sections: [
      {
        id: 'mainArcana',
        title: 'Главный аркан',
        description: 'Твоя главная энергия: сильные стороны, характер и то, что тобой движет.',
        question: 'Расскажи про мой главный аркан — {points[0]}. Что он говорит обо мне как о личности?',
        points: ['center'],
      },
      {
        id: 'innerOuter',
        title: 'Внешнее и внутреннее Я',
        description: 'Какой ты внутри и каким тебя видят окружающие.',
        question:
          'Расскажи про линию моей личности: внутренняя — {points[0]}, основная — {points[1]}, внешняя — {points[2]}. Какая я внутри и какой меня видят люди?',
        points: ['personalityInner', 'personality', 'personalityOuter'],
      },
      {
        id: 'trueSelf',
        title: 'Моё истинное Я',
        description: 'Какой ты на самом деле, когда не нужно соответствовать чужим ожиданиям.',
        question:
          'Расскажи про моё истинное Я: глубинная личность — {points[0]}, главный аркан — {points[1]}. Кто я на самом деле, без масок и социальных ролей?',
        points: ['personalityInner', 'center'],
      },
      {
        id: 'birthEnergies',
        title: 'Энергии дня и года',
        description: 'С чем ты пришёл в этот мир и какие задачи тебе предстоит пройти.',
        question: 'Расскажи про мои энергии рождения: день — {points[0]}, год — {points[1]}. С какими задачами я пришла в этот мир?',
        points: ['dayArcana', 'yearArcana'],
      },
      {
        id: 'monthEnergy',
        title: 'Энергия месяца',
        description: 'Черты характера, которые проявляются в тебе особенно ярко.',
        question: 'Расскажи про энергию моего месяца рождения — {points[0]}. Как она формирует мой характер?',
        points: ['monthArcana'],
      },
      {
        id: 'comfortPoint',
        title: 'Точка комфорта',
        description: 'Что даёт тебе чувство безопасности и помогает восстановиться.',
        question: 'Расскажи про мою точку комфорта — {points[0]}. Что мне даёт равновесие и опору в жизни?',
        points: ['balanceCenter'],
      },
      {
        id: 'personalityTriad',
        title: 'Треугольник характера',
        description: 'Как сочетаются разные стороны твоего характера и где между ними возникают противоречия.',
        question:
          'Расскажи про мой треугольник характера: день — {points[0]}, месяц — {points[1]}, год — {points[2]}. Как эти три энергии вместе формируют мой характер?',
        points: ['dayArcana', 'monthArcana', 'yearArcana'],
      },
    ],
  },
  MATRIX_MISSION_GROUP,
  MATRIX_LOVE_GROUP,
  {
    id: 'ancestry',
    title: 'Род',
    sections: [
      {
        id: 'motherLine',
        title: 'Линия мамы',
        description: 'Что ты переняла от мамы и женщин своего рода.',
        question:
          'Расскажи про мою женскую линию: мама — {points[0]}, бабушки — {points[1]} и {points[2]}. Что мне передалось от мамы и бабушек и как это влияет на мою жизнь?',
        points: ['motherLine', 'g1', 'g2'],
      },
      {
        id: 'fatherLine',
        title: 'Линия папы',
        description: 'Что ты переняла от папы и мужчин своего рода.',
        question:
          'Расскажи про мою мужскую линию: папа — {points[0]}, дедушки — {points[1]} и {points[2]}. Какое наследие я несу от отцовской линии и как это влияет на мою связь с мужским?',
        points: ['fatherLine', 'f1', 'f2'],
      },
      {
        id: 'ancestralGift',
        title: 'Дар рода',
        description: 'Какие сильные стороны достались тебе от предков.',
        question:
          'Расскажи про дар моего рода: от женской линии — {points[0]}, от мужской — {points[1]}. Какие сильные стороны мне передались от предков?',
        points: ['g1', 'f1'],
      },
      {
        id: 'ancestralLesson',
        title: 'Уроки рода',
        description: 'Какие семейные сценарии тебе важно не повторять.',
        question:
          'Расскажи про уроки моего рода: по женской линии — {points[0]}, по мужской — {points[1]}. Что мне важно прожить иначе, чем мои предки?',
        points: ['g2', 'f2'],
      },
      {
        id: 'ancestralWisdom',
        title: 'Мудрость рода',
        description: 'На что ты можешь опереться благодаря своему роду.',
        question:
          'Расскажи про мудрость моего рода: мама — {points[0]}, папа — {points[1]}, прошлый опыт — {points[2]}. Что объединяет обе линии и даёт мне силу?',
        points: ['motherLine', 'fatherLine', 'past'],
      },
      {
        id: 'ancestralGrandmothers',
        title: 'Бабушки и прабабушки',
        description: 'Что передаётся тебе по женской линии из поколения в поколение.',
        question:
          'Расскажи про моих бабушек: первая линия — {points[0]}, вторая линия — {points[1]}. Какие истории и программы несут женщины моего рода в глубину?',
        points: ['g1', 'g2'],
      },
      {
        id: 'ancestralGrandfathers',
        title: 'Дедушки и прадедушки',
        description: 'Что передаётся тебе по мужской линии из поколения в поколение.',
        question:
          'Расскажи про моих дедушек: первая линия — {points[0]}, вторая линия — {points[1]}. Какие истории и программы несут мужчины моего рода в глубину?',
        points: ['f1', 'f2'],
      },
    ],
  },
  MATRIX_MONEY_GROUP,
]
